import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import api from "../../utils/api";

export const fetchMarkedUnitsCases = createAsyncThunk(
  "markedUnits/fetchCases",
  async (
    {
      page = 1,
      limit = 25,
      search = "",
      type = "",
    }: {
      page?: number;
      limit?: number;
      search?: string;
      type?: "" | "live" | "redesign";
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await api.get(
        "/units/delivery-units/marked-cases",
        {
          params: {
            page,
            limit,
            search,
            type,
          },
        }
      );

      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message ||
          "Failed to fetch marked cases"
      );
    }
  }
);

export const fetchRecentlyAssignedUnits = createAsyncThunk(
  "markedUnits/fetchRecentlyAssigned",
  async (
    {
      limit = 50,
      search = "",
    }: {
      limit?: number;
      search?: string;
    } = {},
    { rejectWithValue }
  ) => {
    try {
      const response = await api.get(
        "/units/delivery-units/recently-assigned",
        {
          params: {
            limit,
            search,
          },
        }
      );

      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message ||
          "Failed to fetch recently assigned cases"
      );
    }
  }
);

export const updateMarkedUnits = createAsyncThunk(
  "markedUnits/updateUnits",
  async (
    payload: {
      units_By: string;
      cases: {
        caseId: string;
        caseType: "live" | "redesign";
        newDeliveryUnits: number;
        designUnit?: number;
        patientName?: string | null;
        designer?: string | null;
        captain?: string | null;
        designToothNumbers?: string;
        deliveryToothNumbers?: string;
        filesBy?: string;
        filesUnits?: number;
        model?: boolean | string;
        modelType?: string;
        modelTypes?: string[];
        modelBy?: string;
        modelUnits?: number;
        designerUnits?: Record<string, number>;
        designerWork?: Record<string, "normal" | "model">;
        category?: string;
        crm?: string;
        unitsNotes?: string;
        redesignCategory?: string;
        internalCredits?: {
          case_id: string;
          unitFor: "Design" | "QC" | "Model";
          name: string;
          units: number;
        }[];
        internalRedesigns?: {
          case_id: string;
          designer?: string;
          captain?: string;
          designerUnits?: Record<string, number>;
          designerWork?: Record<string, "normal" | "model">;
          model?: boolean | string;
          modelTypes?: string[];
          modelBy?: string;
          modelUnits?: number;
        }[];
      }[];
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await api.put(
        "/units/delivery-units/marked-update",
        payload
      );

      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message ||
          "Failed to update marked units"
      );
    }
  }
);

interface MarkedUnitsState {
  cases: any[];
  totalCount: number;
  currentPage: number;
  totalPages: number;
  loading: boolean;
  error: string | null;
}

const initialState: MarkedUnitsState = {
  cases: [],
  totalCount: 0,
  currentPage: 1,
  totalPages: 0,
  loading: false,
  error: null,
};

const applyFetchFulfilled = (
  state: MarkedUnitsState,
  action: { payload: any },
) => {
  state.loading = false;
  state.cases = action.payload.data;
  state.totalCount = action.payload.totalCount;
  state.currentPage = action.payload.currentPage;
  state.totalPages = action.payload.totalPages;
};

const markedUnitsSlice = createSlice({
  name: "markedUnits",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchMarkedUnitsCases.pending, (state) => {
        state.loading = true;
        state.error = null;
      })

      .addCase(fetchMarkedUnitsCases.fulfilled, applyFetchFulfilled)

      .addCase(fetchMarkedUnitsCases.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      .addCase(fetchRecentlyAssignedUnits.pending, (state) => {
        state.loading = true;
        state.error = null;
      })

      .addCase(fetchRecentlyAssignedUnits.fulfilled, applyFetchFulfilled)

      .addCase(fetchRecentlyAssignedUnits.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      .addCase(updateMarkedUnits.fulfilled, (state, action) => {
        const updatedCases = action.payload;

        updatedCases.forEach((updated: any) => {
          const target = state.cases.find(
            (c: any) =>
              c.case_id === updated.case_id &&
              c.type === updated.type &&
              (updated.details
                ? c.details === updated.details
                : true)
          );

          if (target) {
            target.delivery_units = updated.delivery_units;
            target.units_By = updated.units_By ?? target.units_By;

            if (updated.designers !== undefined) {
              target.designers = updated.designers;
            }

            if (updated.qc2_users !== undefined) {
              target.qc2_users = updated.qc2_users;
            }

            if (updated.category !== undefined) {
              target.category = updated.category;
            }

            if (updated.crm !== undefined) {
              target.crm = updated.crm;
            }

            if (updated.design_tooth_numbers !== undefined) {
              target.design_tooth_numbers = updated.design_tooth_numbers;
            }

            if (updated.delivery_tooth_numbers !== undefined) {
              target.delivery_tooth_numbers = updated.delivery_tooth_numbers;
            }

            if (updated.files_by !== undefined) {
              target.files_by = updated.files_by;
            }

            if (updated.model !== undefined) {
              target.model = updated.model;
            }

            if (updated.model_type !== undefined) {
              target.model_type = updated.model_type;
            }

            if (updated.model_types !== undefined) {
              target.model_types = updated.model_types;
            }

            if (updated.model_units !== undefined) {
              target.model_units = updated.model_units;
            }

            if (updated.model_by !== undefined) {
              target.model_by = updated.model_by;
            }

            if (updated.designer_units !== undefined) {
              target.designer_units = updated.designer_units;
            }

            if (updated.designer_work !== undefined) {
              target.designer_work = updated.designer_work;
            }

            if (updated.units_notes !== undefined) {
              target.units_notes = updated.units_notes;
            }

            if (updated.redesign_category !== undefined) {
              target.redesign_category = updated.redesign_category;
            }
          }
        });
      })

      .addCase(updateMarkedUnits.rejected, (state, action) => {
        state.error = action.payload as string;
      });
  },
});

export default markedUnitsSlice.reducer;
